// Property validation utility that replicates Laravel validation rules
export const validateProperty = (
  formData,
  isEdit = false,
  propertyId = null
) => {
  const errors = {};

  // Required fields
  if (!formData.name || formData.name.trim() === "") {
    errors.name = "Property name is required";
  } else if (formData.name.length > 100) {
    errors.name = "Property name must not exceed 100 characters";
  }

  if (!formData.property_id || formData.property_id.trim() === "") {
    errors.property_id = "Property ID is required";
  } else if (formData.property_id.length > 30) {
    errors.property_id = "Property ID must not exceed 30 characters";
  }

  if (!formData.status_manual || formData.status_manual === "") {
    errors.status_manual = "Status is required";
  } else {
    const validStatuses = ["999", "9", "0", "4", "5", "6", "11"];
    if (!validStatuses.includes(formData.status_manual)) {
      errors.status_manual = "Invalid status selected";
    }
  }

  // Drives the Building Set Up KPI deadline (14 calendar days residential,
  // 28 commercial), so it must be a deliberate choice rather than a guess.
  if (
    formData.residential_commercial !== undefined &&
    (!formData.residential_commercial || formData.residential_commercial === "")
  ) {
    errors.residential_commercial = "Residential/Commercial is required";
  }

  if (!formData.client_id || formData.client_id === "") {
    errors.client_id = "Client is required";
  } else if (isNaN(formData.client_id)) {
    errors.client_id = "Client must be a valid number";
  }

  // Numeric fields
  if (!formData.price_from || formData.price_from === "") {
    errors.price_from = "Price from is required";
  } else if (isNaN(formData.price_from)) {
    errors.price_from = "Price from must be a number";
  }

  if (!formData.price_to || formData.price_to === "") {
    errors.price_to = "Price to is required";
  } else if (isNaN(formData.price_to)) {
    errors.price_to = "Price to must be a number";
  }

  if (!formData.actual_market_rent || formData.actual_market_rent === "") {
    errors.actual_market_rent = "Actual market rent is required";
  } else if (isNaN(formData.actual_market_rent)) {
    errors.actual_market_rent = "Actual market rent must be a number";
  }

  if (
    formData.square_footage &&
    formData.square_footage !== "" &&
    isNaN(formData.square_footage)
  ) {
    errors.square_footage = "Square footage must be a number";
  }

  if (
    formData.rateable_value &&
    formData.rateable_value !== "" &&
    isNaN(formData.rateable_value)
  ) {
    errors.rateable_value = "Rateable value must be a number";
  }

  if (
    formData.rebuild_value &&
    formData.rebuild_value !== "" &&
    isNaN(formData.rebuild_value)
  ) {
    errors.rebuild_value = "Rebuild value must be a number";
  }

  // Required numeric fields
  if (!formData.spaces || formData.spaces === "") {
    errors.spaces = "Spaces is required";
  } else if (isNaN(formData.spaces)) {
    errors.spaces = "Spaces must be a number";
  }

  if (!formData.bathrooms || formData.bathrooms === "") {
    errors.bathrooms = "Bathrooms is required";
  } else if (isNaN(formData.bathrooms)) {
    errors.bathrooms = "Bathrooms must be a number";
  }

  if (!formData.kitchens || formData.kitchens === "") {
    errors.kitchens = "Kitchens is required";
  } else if (isNaN(formData.kitchens)) {
    errors.kitchens = "Kitchens must be a number";
  }

  // Optional Boolean fields (Laravel: 'in:0,1' - optional but must be 0 or 1 if provided)
  if (
    formData.garden !== "" &&
    formData.garden !== null &&
    formData.garden !== undefined
  ) {
    if (formData.garden !== "0" && formData.garden !== "1") {
      errors.garden = "Garden must be Yes (1) or No (0)";
    }
  }

  if (
    formData.parking !== "" &&
    formData.parking !== null &&
    formData.parking !== undefined
  ) {
    if (formData.parking !== "0" && formData.parking !== "1") {
      errors.parking = "Parking must be Yes (1) or No (0)";
    }
  }

  // VIP rule validation
  if (
    formData.vip_rule_type !== "" &&
    !["0", "1", "2"].includes(formData.vip_rule_type)
  ) {
    errors.vip_rule_type = "Invalid VIP rule type";
  }

  // Unique VIP rule validation (when vip_rule_type === "2")
  if (formData.vip_rule_type === "2") {
    if (!formData.vip_amount || formData.vip_amount === "") {
      errors.vip_amount = "VIP amount is required for unique VIP rule";
    } else if (isNaN(formData.vip_amount)) {
      errors.vip_amount = "VIP amount must be a number";
    }

    if (!formData.vip_provided || formData.vip_provided === "") {
      errors.vip_provided =
        "VIP provided amount is required for unique VIP rule";
    } else if (isNaN(formData.vip_provided)) {
      errors.vip_provided = "VIP provided amount must be a number";
    }

    if (!formData.vip_amount_rule || formData.vip_amount_rule === "") {
      errors.vip_amount_rule =
        "VIP amount rule is required for unique VIP rule";
    }
  }

  // Date validation
  const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/(19|20)\d\d$/;
  const dateIsoRegex = /^(19|20)\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;

  // Helper function to check if a date string is valid in either format
  const isValidDate = (dateString) => {
    if (!dateString || dateString === "") return true; // Empty dates are valid
    return dateRegex.test(dateString) || dateIsoRegex.test(dateString);
  };

  // Helper function to convert date to Date object for comparison
  const parseDate = (dateString) => {
    if (dateRegex.test(dateString)) {
      // DD/MM/YYYY format
      const [day, month, year] = dateString.split("/");
      return new Date(year, month - 1, day);
    } else if (dateIsoRegex.test(dateString)) {
      // YYYY-MM-DD format
      return new Date(dateString);
    }
    return null;
  };

  // Date taken on validation - Laravel conditional rules (matching CreatePropertyRequest.php)
  // Required when status is in: Signed_Proposal (4), Maintenance (5), Empty (10), Not_Full (7), Full (8), Handing_Back (3)
  // Required if date_taken_back is provided
  // For Key_Holding (11), prohibited during creation (backend will check for signed proposal during update)
  const requiredStatusesForDateTakenOn = ["4", "5", "10", "7", "8", "3"]; // Signed_Proposal, Maintenance, Empty, Not_Full, Full, Handing_Back

  if (!formData.date_taken_on || formData.date_taken_on === "") {
    // Check if required based on status (both create and update)
    if (requiredStatusesForDateTakenOn.includes(formData.status_manual)) {
      errors.date_taken_on = "Date taken on is required for this status";
    }
    // Check if required because date_taken_back is provided (both create and update)
    if (formData.date_taken_back && formData.date_taken_back !== "") {
      errors.date_taken_on =
        "Date taken on is required when date taken back is provided";
    }
  } else if (!isValidDate(formData.date_taken_on)) {
    errors.date_taken_on =
      "Date taken on must be in DD/MM/YYYY or YYYY-MM-DD format";
  }

  // Key Holding status - date_taken_on is prohibited during creation (Laravel: prohibited for Key_Holding on create)
  // During update, backend will check if signed proposal exists
  if (formData.status_manual === "11" && !isEdit) {
    if (formData.date_taken_on && formData.date_taken_on !== "") {
      errors.date_taken_on =
        "Date taken on is not allowed for Key Holding status during creation";
    }
  }

  // Key Holding status - date_taken_on is prohibited unless signed proposal exists (backend will check)
  if (
    formData.status_manual === "11" &&
    formData.date_taken_on &&
    formData.date_taken_on !== ""
  ) {
    // Frontend can't check for signed proposal, so we'll let backend handle this
    // Just validate format if provided
    if (!isValidDate(formData.date_taken_on)) {
      errors.date_taken_on =
        "Date taken on must be in DD/MM/YYYY or YYYY-MM-DD format";
    }
  }

  if (
    formData.date_live &&
    formData.date_live !== "" &&
    !isValidDate(formData.date_live)
  ) {
    errors.date_live = "Date live must be in DD/MM/YYYY or YYYY-MM-DD format";
  }

  if (formData.date_taken_back && formData.date_taken_back !== "") {
    if (!isValidDate(formData.date_taken_back)) {
      errors.date_taken_back =
        "Date taken back must be in DD/MM/YYYY or YYYY-MM-DD format";
    } else if (formData.date_taken_on && formData.date_taken_on !== "") {
      // Check if date_taken_back is after date_taken_on
      const takenOn = parseDate(formData.date_taken_on);
      const takenBack = parseDate(formData.date_taken_back);

      if (takenOn && takenBack && takenBack <= takenOn) {
        errors.date_taken_back = "Date taken back must be after date taken on";
      }
    }
  }

  if (
    formData.date_feature_from &&
    formData.date_feature_from !== "" &&
    !isValidDate(formData.date_feature_from)
  ) {
    errors.date_feature_from =
      "Date feature from must be in DD/MM/YYYY or YYYY-MM-DD format";
  }

  if (
    formData.date_feature_to &&
    formData.date_feature_to !== "" &&
    !isValidDate(formData.date_feature_to)
  ) {
    errors.date_feature_to =
      "Date feature to must be in DD/MM/YYYY or YYYY-MM-DD format";
  }

  // Text length validation
  if (formData.short_description && formData.short_description.length > 32768) {
    errors.short_description =
      "Short description must not exceed 32768 characters";
  }

  if (formData.address_1 && formData.address_1.length > 255) {
    errors.address_1 = "Address 1 must not exceed 255 characters";
  }

  if (formData.address_2 && formData.address_2.length > 255) {
    errors.address_2 = "Address 2 must not exceed 255 characters";
  }

  if (formData.address_3 && formData.address_3.length > 255) {
    errors.address_3 = "Address 3 must not exceed 255 characters";
  }

  if (formData.town && formData.town.length > 255) {
    errors.town = "Town must not exceed 255 characters";
  }

  if (formData.postcode && formData.postcode.length > 255) {
    errors.postcode = "Postcode must not exceed 255 characters";
  }

  // Video URL validation (optional, but must be a valid YouTube URL when provided)
  if (formData.video && formData.video.trim() !== "") {
    const videoUrl = formData.video.trim();
    const youtubePatterns = [
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com)\/embed\/[\w-]{6,}/i,
      /^(https?:\/\/)?youtu\.be\/[\w-]{6,}/i,
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com)\/shorts\/[\w-]{6,}/i,
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com)\/v\/[\w-]{6,}/i,
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com)\/watch\?[^#]*\bv=[\w-]{6,}/i,
    ];
    if (!youtubePatterns.some((pattern) => pattern.test(videoUrl))) {
      errors.video =
        "Video must be a valid YouTube URL (e.g. https://www.youtube.com/watch?v=...)";
    } else if (videoUrl.length > 255) {
      errors.video = "Video URL must be at most 255 characters";
    }
  }

  // Client contact validation
  if (
    formData.client_contact_id &&
    formData.client_contact_id !== "" &&
    isNaN(formData.client_contact_id)
  ) {
    errors.client_contact_id = "Client contact must be a valid number";
  }

  // Client type validation
  if (
    formData.client_type &&
    formData.client_type !== "" &&
    isNaN(formData.client_type)
  ) {
    errors.client_type = "Client type must be a valid number";
  }

  // Location ID validation - Laravel: 'integer|exists:property_locations,id'
  // Frontend can only check if it's a valid integer, existence check is done in backend
  if (
    formData.location_id &&
    formData.location_id !== "" &&
    formData.location_id !== null
  ) {
    if (
      isNaN(formData.location_id) ||
      !Number.isInteger(parseFloat(formData.location_id))
    ) {
      errors.location_id = "Location must be a valid integer";
    }
  }

  // Type ID validation - Laravel: 'integer|exists:property_types,id'
  // Frontend can only check if it's a valid integer, existence check is done in backend
  if (
    formData.type_id &&
    formData.type_id !== "" &&
    formData.type_id !== null
  ) {
    if (
      isNaN(formData.type_id) ||
      !Number.isInteger(parseFloat(formData.type_id))
    ) {
      errors.type_id = "Property type must be a valid integer";
    }
  }

  // Note: property_id unique check is handled in backend (Laravel: 'unique:properties,property_id,{id}')
  // Frontend cannot check uniqueness without API call, so backend will handle this

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Helper function to format validation errors for display
export const formatValidationErrors = (errors) => {
  return Object.entries(errors).map(([field, message]) => ({
    field,
    message,
  }));
};

// Helper function to get field error class
export const getFieldErrorClass = (errors, fieldName) => {
  return errors[fieldName] ? "form-control is-invalid" : "form-control";
};

// Helper function to get field error message
export const getFieldErrorMessage = (errors, fieldName) => {
  return errors[fieldName] || "";
};
